import React, { useEffect, useState, useRef, createElement } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../../components/ui/Button';
import { SalesReceipt } from '../../types';
import { IssuerHeader } from './IssuerHeader';
import { calculateSalesReceiptTotals, formatCurrency } from './utils';
export type SalesReceiptDocumentAction = 'view' | 'print' | 'download';
type SalesReceiptModalProps = {
  receipt: SalesReceipt;
  onClose: () => void;
  initialAction?: SalesReceiptDocumentAction;
};
export function SalesReceiptModal({
  receipt,
  onClose,
  initialAction = 'view'
}: SalesReceiptModalProps) {
  const printableRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const totals = calculateSalesReceiptTotals(receipt.items);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const printReceipt = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => document.body.classList.remove('printing-test-modal'), 500);
  };
  const downloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    try {
      await captureToA4Pdf(printableRef.current, `BMED_${receipt.salesReceiptNumber.replace(/[^a-z0-9-_]/gi, '_')}.pdf`);
    } catch (error) {
      console.error('PDF generation failed', error);
      window.alert('Failed to generate the PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  useEffect(() => {
    if (initialAction === 'print') {
      const timer = window.setTimeout(printReceipt, 0);
      return () => window.clearTimeout(timer);
    }
    if (initialAction === 'download') {
      const timer = window.setTimeout(() => void downloadPdf(), 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, []);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 print:bg-white print:p-0" role="dialog" aria-modal="true" aria-labelledby="sales-receipt-modal-title" data-id="element-1916">
      <div className="test-modal-print max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:shadow-none" data-id="element-1917">
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3 print:hidden" data-id="element-1918">
          <h2 id="sales-receipt-modal-title" className="text-lg font-semibold text-slate-900" data-id="element-1919">
            Sales Receipt {receipt.salesReceiptNumber}
          </h2>
          <div className="flex items-center gap-2" data-id="element-1920">
            <Button variant="outline" size="sm" onClick={printReceipt} data-id="element-1921">
              <PrinterIcon className="mr-1 h-4 w-4" data-id="element-1922" /> Print
            </Button>
            <Button size="sm" onClick={downloadPdf} disabled={isGeneratingPdf} data-id="element-1923">
              <DownloadIcon className="mr-1 h-4 w-4" data-id="element-1924" />
              {isGeneratingPdf ? 'Generating…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close sales receipt" data-id="element-1925">
              <XIcon className="h-5 w-5" data-id="element-1926" />
            </button>
          </div>
        </div>

        <article ref={printableRef} className="billing-document bg-white p-0 text-slate-950" data-id="element-1927">
          <header className="flex flex-col gap-6 border-b-2 border-slate-900 pb-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-1928">
            <IssuerHeader data-id="element-1929" />
            <div className="text-left sm:text-right" data-id="element-1930">
              <h1 className="text-2xl font-bold tracking-[0.14em] text-slate-800 sm:text-3xl" data-id="element-1931">
                SALES RECEIPT
              </h1>
              <p className="mt-3 font-semibold" data-id="element-1932">
                Sales Receipt# {receipt.salesReceiptNumber}
              </p>
            </div>
          </header>

          <section className="my-7 grid gap-5 border-b border-slate-200 pb-7 sm:grid-cols-2" data-id="element-1933">
            <div data-id="element-1934">
              <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1935">
                Bill to
              </h2>
              <p className="text-lg font-bold text-slate-900" data-id="element-1936">
                {receipt.billTo}
              </p>
              {receipt.customerPhone && <p className="mt-1 text-slate-600" data-id="element-1937">{receipt.customerPhone}</p>}
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 sm:justify-self-end" data-id="element-1938">
              <dt className="font-semibold text-slate-600" data-id="element-1939">Number</dt>
              <dd className="font-mono font-semibold text-slate-900" data-id="element-1940">
                {receipt.salesReceiptNumber}
              </dd>
              <dt className="font-semibold text-slate-600" data-id="element-1941">Visit Date</dt>
              <dd data-id="element-1942">{formatDate(receipt.visitDate)}</dd>
              <dt className="font-semibold text-slate-600" data-id="element-1943">Sales Person</dt>
              <dd data-id="element-1944">{receipt.salespersonName}</dd>
            </dl>
          </section>

          <div className="overflow-x-auto" data-id="element-1945">
            <table className="min-w-[680px] w-full border-collapse" data-id="element-1946">
              <thead data-id="element-1947">
                <tr className="border-b-2 border-slate-300 bg-slate-100" data-id="element-1948">
                  <th scope="col" className="w-16 p-2.5 text-left text-xs font-bold uppercase text-slate-600" data-id="element-1949">
                    No.
                  </th>
                  <th scope="col" className="p-2.5 text-left text-xs font-bold uppercase text-slate-600" data-id="element-1950">
                    Item &amp; Description
                  </th>
                  <th scope="col" className="w-20 p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1951">
                    Qty
                  </th>
                  <th scope="col" className="w-32 p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1952">
                    Rate
                  </th>
                  <th scope="col" className="w-36 p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1953">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1954">
                {receipt.items.map((item, index) => <tr key={item.id} className="border-b border-slate-200" data-id="element-1955">
                    <td className="p-2.5 text-slate-600" data-id="element-1956">{index + 1}</td>
                    <td className="p-2.5 font-medium text-slate-900" data-id="element-1957">
                      {item.itemDescription}
                    </td>
                    <td className="p-2.5 text-right" data-id="element-1958">{item.quantity}</td>
                    <td className="p-2.5 text-right" data-id="element-1959">
                      {formatCurrency(item.rate)}
                    </td>
                    <td className="p-2.5 text-right font-semibold" data-id="element-1960">
                      {formatCurrency(item.quantity * item.rate)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <dl className="ml-auto mt-7 w-full max-w-xs" data-id="element-1961">
            <div className="flex justify-between py-1" data-id="element-1962">
              <dt className="text-slate-600" data-id="element-1963">Subtotal:</dt>
              <dd className="font-medium" data-id="element-1964">{formatCurrency(totals.subtotal)}</dd>
            </div>
            <div className="flex justify-between border-b border-slate-300 py-1" data-id="element-1965">
              <dt className="text-slate-600" data-id="element-1966">TOT Tax (5%):</dt>
              <dd className="font-medium" data-id="element-1967">{formatCurrency(totals.totalTax)}</dd>
            </div>
            <div className="flex justify-between py-3 text-lg font-bold" data-id="element-1968">
              <dt data-id="element-1969">Total:</dt>
              <dd className="text-blue-700" data-id="element-1970">
                {formatCurrency(totals.totalAmount)}
              </dd>
            </div>
          </dl>

          <footer className="mt-14 flex items-end justify-between border-t border-slate-200 pt-5 text-xs text-slate-500" data-id="element-1971">
            <p data-id="element-1972">Independent non-invoice cash sale.</p>
            <div className="w-44 border-t border-slate-800 pt-1 text-center font-semibold uppercase text-slate-700" data-id="element-1973">
              Authorized Signature
            </div>
          </footer>
        </article>
      </div>
    </div>;
}
function formatDate(value: string) {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 17.78;
  const marginRight = 10.16;
  const marginBottom = 17.78;
  const marginLeft = 12.7;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const pixelsPerMillimetre = sourceCanvas.width / contentWidth;
  const sliceHeight = Math.floor(contentHeight * pixelsPerMillimetre);
  let cursor = 0;
  let page = 0;
  while (cursor < sourceCanvas.height) {
    const height = Math.min(sliceHeight, sourceCanvas.height - cursor);
    const slice = document.createElement('canvas');
    slice.width = sourceCanvas.width;
    slice.height = height;
    const context = slice.getContext('2d');
    if (!context) break;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(sourceCanvas, 0, cursor, sourceCanvas.width, height, 0, 0, slice.width, height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginLeft, marginTop, contentWidth, height / pixelsPerMillimetre);
    cursor += height;
    page += 1;
  }
  pdf.save(filename);
}